import React, { Component, useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { IGetRepGroupsDTO } from "../../../models/DTO/IGetRepGroupsDTO";
import IGetSalesRepsDTO from "../../../models/DTO/IGetSalesRepsDTO";
import IOnGetRepGroupsDTO from "../../../models/DTO/IOnGetRepGroupsDTO";
import IOnGetSalesRepsDTO from "../../../models/DTO/IOnGetSalesRepsDTO";
import IOnProcessRepGroupDTO from "../../../models/DTO/IOnProcessRepGroupDTO";
import IOnProcessSalesRepDTO from "../../../models/DTO/IOnProcessSalesRepDTO";
import { IProcessRepGroupDTO } from "../../../models/DTO/IProcessRepGroupDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IAction from "../../../models/IAction";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IOwTableCols from "../../../models/IOwTableCols";
import IRepGroup from "../../../models/IRepGroup";
import ISalesRep from "../../../models/ISalesRep";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import FishbowlAssignUser from "../../shared/FishbowlAssignUser/FishbowlAssignUser";
import OwActionBar from "../../shared/OwActionBar/OwActionBar";
import OwButton from "../../shared/OwButton/OwButton";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { RepGroupRepsDetail } from "../RepGroupRepsDetail/RepGroupRepsDetail";
import { RepGroupsAssignedVendors } from "../RepGroupsAssignedVendors/RepGroupsAssignedVendors";
import { RepGroupSidebar } from "../RepGroupSidebar/RepGroupSidebar";
import SalesRepSidebar from "../SalesRepSidebar/SalesRepSidebar";
import IntegrationsConnectedBar from "../../shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {};

export const RepGroups: React.FC<Props> = (props: Props) => {
    const { socket, userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [modalComponent, setModalComponent] = useState<any>(null);

    const overlayRef: React.Ref<HTMLElement> = useRef<HTMLElement | null>(null);
    const breadcrumbs: IBreadcrumbs[] = [
        { title: "dashboard", url: "/dashboard" },
        { title: "rep_groups", url: "/rep-groups" },
    ];

    const showAssignFishbowlUserModal = () => {
        const checkedRowGuid = getCheckedRowGuid();

        const repGroup = repGroups.find((group) => {
            return group.guid === checkedRowGuid;
        });

        const _modalComponent = (
            <FishbowlAssignUser
                closeModalFn={closeModal}
                socket={socket}
                notifyFn={uiContext.notifyFn}
                userModel={userModel}
                supplierModel={supplierModel}
                repGroup={repGroup}
            />
        );

        setModalComponent(_modalComponent);
        setModalIsOpen(true);
    };

    const hasOnlyOneChecked = (): boolean => {
        const keys = Object.keys(checkedRows);

        let _hasOnlyOneChecked = true;

        let count = 0;

        for (const key of keys) {
            if (checkedRows[key]) {
                count++;
            }
        }

        if (count > 1) {
            _hasOnlyOneChecked = false;
        }

        if (!count) {
            _hasOnlyOneChecked = false;
        }

        return !_hasOnlyOneChecked;
    };

    const getActionBar = () => {
        const barItems: IAction[] = [];

        if (supplierModel.Supplier_Integration.has_fishbowl) {
            barItems.push({
                name: "fishbowl",
                isGroup: true,
                groupActions: [
                    {
                        name: "assign_user",
                        fn: showAssignFishbowlUserModal,
                        disabledFn: hasOnlyOneChecked,
                    },
                ],
            });
        }

        return barItems;
    };

    const actionBarActions: IAction[] = getActionBar();

    const renderActionsCol = (row: IRepGroup, index: number) => {
        return (
            <div className={"d-flex align-items-center"}>
                <OwCheckbox name={row.guid} truthyVal={true} falsyVal={false} fn={setChecked} checked={checkedRows[row.guid]} />
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        toggleDelete(row, index);
                    }}
                    onEditClickFn={() => {
                        editRow(row, index);
                    }}
                />
            </div>
        );
    };

    const renderIntegrationsFn = (row: IRepGroup) => {
        return (
            <IntegrationsConnectedBar
                supplierModel={supplierModel}
                showIntegrations={{
                    quickbooksDesktop: supplierModel.Supplier_Integration.has_qbd,
                }}
                qbdConnected={!!row.qbd_SalesRepId}
            />
        );
    };

    const columns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "",
            isActionsCol: true,
            renderFn: renderActionsCol,
            renderFnEmitsRow: true,
            showChevronFn: (row: IRepGroup) => {
                return true;
            },
            hasChevron: true,
        },
        {
            title: "name",
            valueProp: "name",
        },
        {
            title: "email",
            valueProp: "email",
        },
        {
            title: "",
            skipTitleTranslate: true,
            valueProp: "",
            renderFn: renderIntegrationsFn,
            renderFnEmitsRow: true,
        },
    ];

    const [isInitialData, setIsInitialData] = useState<boolean>(true);
    const [isDataFetching, setIsDataFetching] = useState<boolean>(false);
    const [limit, setLimit] = useState<number>(25);
    const [offset, setOffset] = useState<number>(0);
    const [repGroups, setRepGroups] = useState<IRepGroup[]>([]);
    const [unclaimedSalesReps, setUnclaimedSalesReps] = useState<ISalesRep[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [sourceElement, setSourceElement] = useState<any>(null);
    const [sourceElementId, setSourceElementId] = useState<string>("");
    const [sourceRepGroup, setSourceRepGroup] = useState<IRepGroup | undefined>(undefined);

    const [checkedRows, setCheckedRows] = useState<{ [key: string]: boolean }>({});

    const getRepGroups = () => {
        const payload: IGetRepGroupsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit,
            offset,
        };

        socket.emit(EndpointsEnum.RepGroupGetRepGroups, payload);

        setIsDataFetching(true);
    };

    const onGetRepGroups = (data: IOnGetRepGroupsDTO) => {
        setRepGroups(data.repGroups);
        setTotalRecords(data.totalRepGroups);
        setIsDataFetching(false);
        setIsInitialData(false);
    };

    const getSalesReps = () => {
        const payload: IGetSalesRepsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit,
            offset,
        };

        socket.emit(EndpointsEnum.RepGroupGetSalesReps, payload);
    };

    const onGetSalesReps = (data: IOnGetSalesRepsDTO) => {
        const _unclaimedSalesReps: ISalesRep[] = [];

        for (const salesRep of data.salesReps) {
            if (!salesRep.rep_group_id) {
                _unclaimedSalesReps.push(salesRep);
            }
        }

        setUnclaimedSalesReps(_unclaimedSalesReps);
    };

    const onProcessSalesRep = (data: IOnProcessSalesRepDTO) => {
        getRepGroups();
    };

    const onProcessRepGroup = (data: IOnProcessRepGroupDTO) => {
        getRepGroups();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.RepGroupOnGetRepGroups, fn: onGetRepGroups },
        { endpoint: EndpointsEnum.RepGroupOnGetSalesReps, fn: onGetSalesReps },
        { endpoint: EndpointsEnum.RepGroupOnProcessSalesRep, fn: onProcessSalesRep },
        { endpoint: EndpointsEnum.RepGroupOnProcessRepGroup, fn: onProcessRepGroup },
        { endpoint: EndpointsEnum.RepGroupOnProcessAssignedVendors, fn: getRepGroups },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getRepGroups();
        getSalesReps();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        getRepGroups();
    }, [limit, offset]);

    const toggleDelete = (row: IRepGroup, index: number) => {
        const sourceElem = document.getElementById(`row-${index}`);

        setSourceElement(sourceElem);
        setSourceElementId(`row-${index}`);
        setSourceRepGroup(row);
    };

    const editRow = (row: IRepGroup, index: number) => {
        createSidebar(row);
    };

    const getCheckedRowGuid = (): string => {
        const checkedItems = Object.keys(checkedRows);

        let rowGuid = "";

        for (const checkedItem of checkedItems) {
            if (checkedRows[checkedItem]) {
                rowGuid = checkedItem;
            }
        }

        return rowGuid;
    };

    const setChecked = (e: any) => {
        const checkedGuid = e.target.name;

        const _checkedRows = checkedRows;

        // only allow 1 row to be checked
        for (const key of Object.keys(checkedRows)) {
            if (_checkedRows[key]) {
                _checkedRows[key] = false;
            }
        }

        _checkedRows[checkedGuid] = e.target.value;

        setCheckedRows(_checkedRows);
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const cancel = (refreshData?: boolean) => {
        if (refreshData) {
            getRepGroups();
            getSalesReps();
        }

        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const createSidebar = (repGroup?: IRepGroup) => {
        const component = <RepGroupSidebar cancelFn={cancel} repGroup={repGroup} />;

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const createSalesRep = (repGroup: IRepGroup) => {
        const component = (
            <SalesRepSidebar
                setActivityIndicator={uiContext.setActivityIndicator}
                socket={socket}
                supplierModel={supplierModel}
                userModel={userModel}
                cancelFn={cancel}
                notifyFn={uiContext.notifyFn}
                repGroup={repGroup}
            />
        );

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const getDetailView = (row: IRepGroup, index: number) => {
        const component = (
            <div>
                <RepGroupRepsDetail cancelSidebar={cancel} salesReps={row.Sales_Reps || []} repGroup={row} parentRowIndex={index} />

                <div>
                    <RepGroupsAssignedVendors repGroup={row} parentRowIndex={index} />
                </div>
            </div>
        );

        return component;
    };

    const cancelDelete = () => {
        setSourceRepGroup(undefined);
        setSourceElement(null);
        setSourceElementId("");
    };

    const commitDelete = () => {
        if (sourceRepGroup) {
            const payload: IProcessRepGroupDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                repGroup: sourceRepGroup,
                deleteFlag: true,
            };

            socket.emit(EndpointsEnum.RepGroupProcessRepGroup, payload);

            setSourceElement(null);
        }
    };

    const closeModal = () => {
        setModalComponent(null);
        setModalIsOpen(false);
    };

    return (
        <div className={"RepGroups position-relative"} id={"rep-groups"}>
            {/*<Breadcrumbs navItems={breadcrumbs} />*/}

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"rep_groups"} leftIconName={"fad fa-users-class"} />
                    <div>
                        <OwButton
                            className={"btn-warning ms-2"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                createSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div>
                <OwActionBar
                    toggleSmallTable={() => {}}
                    colToggledFn={() => {}}
                    columns={{}}
                    hideColumnToggle={true}
                    actions={actionBarActions}
                />
            </div>

            <div className="row">
                <div className="col-12">
                    <div className={"table-responsive position-relative"}>
                        <ConfirmDeleteOverlayFn
                            targetElem={sourceElementId}
                            handleConfirm={commitDelete}
                            handleCancel={cancelDelete}
                            skipMainView={true}
                            nestedParentElem={"rep-group-table"}
                        />

                        <OwTable
                            id={"rep-group-table"}
                            columns={columns}
                            dataSet={repGroups}
                            userModel={userModel}
                            supplierModel={supplierModel}
                            dataFetch={isDataFetching}
                            initialData={isInitialData}
                            detailViewComponent={getDetailView}
                            tableConfig={{ hasDetailView: true }}
                        />
                    </div>

                    <div>
                        <PaginationBar offset={offset} limit={limit} totalRecords={totalRecords} onPaginationChanged={paginationChanged} />
                    </div>
                </div>
            </div>

            <div>
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel="Orderwerks"
                    shouldCloseOnOverlayClick={false}
                    className={"modalContentClass"}
                    overlayClassName={"modalOverlayClass"}
                    style={{}}>
                    {modalComponent}
                </Modal>
            </div>
        </div>
    );
};
